<template>
  <div class="my-jobs-page">
    <div class="page-header">
      <h1 class="page-title">내 공고 관리</h1>
      <p class="page-subtitle">등록한 공고 {{ myJobs.length }}개</p>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
    </div>

    <div v-else-if="myJobs.length === 0" class="empty-state">
      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" stroke-width="1.5">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="12" y1="18" x2="12" y2="12"/>
        <line x1="9" y1="15" x2="15" y2="15"/>
      </svg>
      <p>등록한 공고가 없어요</p>
      <router-link to="/jobs/create" class="btn-primary">공고 등록하기</router-link>
    </div>

    <template v-else>
      <!-- 새 공고 등록 버튼 -->
      <div class="action-bar">
        <router-link to="/jobs/create" class="btn-create">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          새 공고 등록
        </router-link>
      </div>

      <!-- 공고 목록 -->
      <div class="job-list">
        <div v-for="job in myJobs" :key="job.id" class="job-item">
          <router-link :to="`/jobs/${job.id}`" class="job-link">
            <div class="job-thumbnail">
              <img :src="job.thumbnail || 'https://picsum.photos/640/360?random=' + job.id" :alt="job.siteName" />
            </div>
            <div class="job-info">
              <div class="job-meta">
                <span class="tag" :class="{ premium: job.isPremium }">
                  {{ job.isPremium ? 'PREMIUM' : '일반' }}
                </span>
                <span class="tag">{{ job.region }}</span>
                <span class="tag">{{ job.industry }}</span>
              </div>
              <h3 class="job-title">{{ job.title }}</h3>
              <p class="job-site">{{ job.siteName }} · {{ job.jobType }}</p>
              <div class="job-salary">{{ job.salaryType }} {{ job.salary }}</div>
              <div class="job-stats">
                <span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  {{ job.views || 0 }}
                </span>
                <span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                  {{ job.bookmarkCount || 0 }}
                </span>
                <span>{{ formatDate(job.createdAt) }}</span>
              </div>
              
              <!-- 업그레이드 상태 -->
              <div v-if="hasActiveUpgrade(job)" class="upgrade-status">
                <div v-if="job.isPremium && job.isPremiumUntil" class="upgrade-badge premium">
                  <span class="upgrade-name">프리미엄</span>
                  <span class="upgrade-days">{{ getDaysLeft(job.isPremiumUntil) }}</span>
                </div>
                <div v-if="job.isPick && job.isPickUntil" class="upgrade-badge pick">
                  <span class="upgrade-name">PICK</span>
                  <span class="upgrade-days">{{ getDaysLeft(job.isPickUntil) }}</span>
                </div>
                <div v-if="job.isUrgent && job.isUrgentUntil" class="upgrade-badge urgent">
                  <span class="upgrade-name">급구</span>
                  <span class="upgrade-days">{{ getDaysLeft(job.isUrgentUntil) }}</span>
                </div>
                <div v-if="job.isFeatured && job.isFeaturedUntil" class="upgrade-badge featured">
                  <span class="upgrade-name">추천</span>
                  <span class="upgrade-days">{{ getDaysLeft(job.isFeaturedUntil) }}</span>
                </div>
                <div v-if="job.isRegionAd && job.isRegionAdUntil" class="upgrade-badge region">
                  <span class="upgrade-name">📍 지역</span>
                  <span class="upgrade-days">{{ getDaysLeft(job.isRegionAdUntil) }}</span>
                </div>
              </div>

              <!-- 배너 광고 상태 -->
              <div v-if="hasActiveBanners(job.id)" class="banner-status">
                <div 
                  v-for="banner in jobBanners[job.id]" 
                  :key="banner.type" 
                  class="banner-badge"
                  :style="{ background: banner.color + '15', color: banner.color }"
                >
                  <span class="banner-name">{{ banner.name }}</span>
                  <span class="banner-days" :style="{ background: banner.color + '20' }">{{ getDaysLeft(banner.expiresAt) }}</span>
                </div>
              </div>
            </div>
          </router-link>
          
          <div class="job-actions">
            <router-link :to="`/jobs/${job.id}/edit`" class="btn-edit">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
              수정
            </router-link>
            <button class="btn-delete" @click="handleDelete(job)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                <line x1="10" y1="11" x2="10" y2="17"/>
                <line x1="14" y1="11" x2="14" y2="17"/>
              </svg>
              삭제
            </button>
          </div>
        </div>
      </div>
    </template>

    <!-- 삭제 확인 모달 -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="showDeleteModal = false">
      <div class="modal-content" @click.stop>
        <h3 class="modal-title">공고 삭제</h3>
        <p class="modal-desc">
          "{{ jobToDelete?.title }}" 공고를 삭제하시겠습니까?<br>
          삭제된 공고는 복구할 수 없습니다.
        </p>
        <div class="modal-actions">
          <button class="btn-cancel" @click="showDeleteModal = false">취소</button>
          <button class="btn-confirm" @click="confirmDelete" :disabled="deleting">
            {{ deleting ? '삭제 중...' : '삭제' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { db } from '@/services/firebase'
import { useAuth } from '@/composables/useAuth'
import { useJobs } from '@/composables/useJobs'

export default {
  name: 'MyJobsView',
  setup() {
    const { user } = useAuth()
    const { getMyJobs, deleteJob } = useJobs()

    const myJobs = ref([])
    const jobBanners = ref({})
    const loading = ref(true)
    const showDeleteModal = ref(false)
    const jobToDelete = ref(null)
    const deleting = ref(false)

    const fetchMyJobs = async () => {
      if (!user.value) {
        loading.value = false
        return
      }

      loading.value = true
      try {
        myJobs.value = await getMyJobs(user.value.uid)
        await fetchJobBanners()
      } catch (e) {
        console.error('내 공고 로드 실패:', e)
      } finally {
        loading.value = false
      }
    }

    // 공고별 배너 정보 로드
    const fetchJobBanners = async () => {
      if (!user.value || myJobs.value.length === 0) return

      const bannerCollections = [
        { id: 'mainBanner', name: '메인배너', collection: 'banners', color: '#3182f6' },
        { id: 'topBanner', name: '상단띠배너', collection: 'topBanners', color: '#f59e0b' },
        { id: 'subBanner', name: '서브배너', collection: 'subBanners', color: '#8b5cf6' },
        { id: 'sidebarAd', name: '사이드바', collection: 'sidebarAds', color: '#10b981' }
      ]

      const result = {}
      const now = new Date()

      for (const job of myJobs.value) {
        result[job.id] = []
        
        for (const banner of bannerCollections) {
          try {
            const q = query(
              collection(db, banner.collection),
              where('jobId', '==', job.id),
              where('isActive', '==', true)
            )
            const snapshot = await getDocs(q)
            
            // 만료되지 않은 배너 찾기
            for (const doc of snapshot.docs) {
              const data = doc.data()
              if (data.expiresAt) {
                const expiry = data.expiresAt.toDate ? data.expiresAt.toDate() : new Date(data.expiresAt)
                if (expiry > now) {
                  result[job.id].push({
                    type: banner.id,
                    name: banner.name,
                    color: banner.color,
                    expiresAt: data.expiresAt
                  })
                }
              }
            }
          } catch (e) {
            console.error(`배너 로드 실패 (${banner.collection}):`, e)
          }
        }
      }

      jobBanners.value = result
    }

    const formatDate = (date) => {
      if (!date) return '-'
      if (date.toDate) {
        date = date.toDate()
      }
      return new Date(date).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }

    const handleDelete = (job) => {
      jobToDelete.value = job
      showDeleteModal.value = true
    }

    const confirmDelete = async () => {
      if (!jobToDelete.value) return

      deleting.value = true
      try {
        await deleteJob(jobToDelete.value.id)
        myJobs.value = myJobs.value.filter(job => job.id !== jobToDelete.value.id)
        showDeleteModal.value = false
        jobToDelete.value = null
      } catch (e) {
        console.error('공고 삭제 실패:', e)
      } finally {
        deleting.value = false
      }
    }

    // 활성화된 업그레이드가 있는지 확인
    const hasActiveUpgrade = (job) => {
      return (job.isPremium && job.isPremiumUntil) ||
             (job.isPick && job.isPickUntil) ||
             (job.isUrgent && job.isUrgentUntil) ||
             (job.isFeatured && job.isFeaturedUntil) ||
             (job.isRegionAd && job.isRegionAdUntil)
    }

    // 활성화된 배너가 있는지 확인
    const hasActiveBanners = (jobId) => {
      return jobBanners.value[jobId]?.length > 0
    }

    // 남은 일수 계산
    const getDaysLeft = (untilDate) => {
      if (!untilDate) return ''
      
      let date = untilDate
      if (untilDate.toDate) {
        date = untilDate.toDate()
      } else if (untilDate.seconds) {
        date = new Date(untilDate.seconds * 1000)
      } else {
        date = new Date(untilDate)
      }
      
      const now = new Date()
      const diff = date - now
      const days = Math.ceil(diff / (1000 * 60 * 60 * 24))
      
      if (days < 0) return '만료됨'
      if (days === 0) return 'D-Day'
      return `D-${days}`
    }

    onMounted(() => {
      if (user.value) {
        fetchMyJobs()
      }
    })

    watch(user, (newVal) => {
      if (newVal) {
        fetchMyJobs()
      }
    })

    return {
      myJobs,
      jobBanners,
      loading,
      showDeleteModal,
      jobToDelete,
      deleting,
      formatDate,
      handleDelete,
      confirmDelete,
      hasActiveUpgrade,
      hasActiveBanners,
      getDaysLeft
    }
  }
}
</script>

<style scoped>
.my-jobs-page {
  max-width: 100%;
}

.page-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 0 0 32px 0;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #191f28;
  margin: 0 0 6px 0;
}

.page-subtitle {
  font-size: 14px;
  color: #6b7684;
  margin: 0;
}

.loading-state {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f2f4f6;
  border-top-color: #3182f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-state p {
  font-size: 16px;
  color: #6b7684;
  margin: 20px 0 24px;
}

.btn-primary {
  padding: 14px 28px;
  background: #3182f6;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  border-radius: 10px;
}

.action-bar {
  margin-bottom: 24px;
}

.btn-create {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #3182f6;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  border-radius: 10px;
  transition: background 0.15s;
}

.btn-create:hover {
  background: #1b64da;
}

.job-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.job-item {
  display: flex;
  background: #fff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: all 0.2s;
}

.job-item:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
}

.job-link {
  display: flex;
  flex: 1;
  min-width: 0;
}

.job-thumbnail {
  width: 240px;
  min-width: 240px;
  aspect-ratio: 16 / 9;
  background: #f2f4f6;
  overflow: hidden;
}

.job-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #f2f4f6;
}

.job-info {
  flex: 1;
  min-width: 0;
  padding: 20px;
  display: flex;
  flex-direction: column;
}

.job-meta {
  display: flex;
  gap: 6px;
  margin-bottom: 10px;
}

.tag {
  padding: 4px 8px;
  background: #f2f4f6;
  color: #6b7684;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
}

.tag.premium {
  background: #3182f6;
  color: #fff;
}

.job-title {
  font-size: 17px;
  font-weight: 600;
  color: #191f28;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.job-site {
  font-size: 14px;
  color: #6b7684;
  margin-bottom: 8px;
}

.job-salary {
  font-size: 15px;
  font-weight: 700;
  color: #3182f6;
  margin-bottom: 12px;
}

.job-stats {
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: #8b95a1;
  margin-top: auto;
}

.job-stats span {
  display: flex;
  align-items: center;
  gap: 4px;
}

.job-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px;
  border-left: 1px solid #f2f4f6;
}

.btn-edit,
.btn-delete {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-edit {
  background: #f2f4f6;
  color: #4e5968;
  border: none;
}

.btn-edit:hover {
  background: #e5e8eb;
  color: #191f28;
}

.btn-delete {
  background: #fff;
  color: #e03131;
  border: 1px solid #ffc9c9;
}

.btn-delete:hover {
  background: #fff5f5;
  border-color: #e03131;
}

/* 삭제 모달 */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 24px;
}

.modal-content {
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  max-width: 400px;
  width: 100%;
}

.modal-title {
  font-size: 20px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 12px;
}

.modal-desc {
  font-size: 15px;
  color: #4e5968;
  line-height: 1.6;
  margin-bottom: 24px;
}

.modal-actions {
  display: flex;
  gap: 12px;
}

.btn-cancel {
  flex: 1;
  padding: 14px;
  background: #f2f4f6;
  color: #4e5968;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-cancel:hover {
  background: #e5e8eb;
}

.btn-confirm {
  flex: 1;
  padding: 14px;
  background: #e03131;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-confirm:hover:not(:disabled) {
  background: #c92a2a;
}

.btn-confirm:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 업그레이드 상태 */
.upgrade-status {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e5e8eb;
}

.upgrade-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 12px;
}

.upgrade-badge.premium {
  background: #e0efff;
  color: #3182f6;
}

.upgrade-badge.pick {
  background: #f3e8ff;
  color: #a855f7;
}

.upgrade-badge.urgent {
  background: #fee2e2;
  color: #ef4444;
}

.upgrade-badge.featured {
  background: #dcfce7;
  color: #16a34a;
}

.upgrade-badge.region {
  background: #dbeafe;
  color: #3182f6;
}

.upgrade-name {
  font-weight: 600;
}

.upgrade-days {
  font-weight: 700;
  padding: 2px 6px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}

/* 배너 광고 상태 */
.banner-status {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.banner-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 12px;
}

.banner-name {
  font-weight: 600;
}

.banner-days {
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
}

/* 반응형 */
@media (max-width: 1024px) {
  .job-thumbnail {
    width: 200px;
    min-width: 200px;
  }
}

@media (max-width: 768px) {
  .page-title {
    font-size: 22px;
  }

  .page-subtitle {
    font-size: 13px;
  }

  .job-item {
    flex-direction: column;
  }

  .job-link {
    flex-direction: column;
  }

  .job-thumbnail {
    width: 100%;
    min-width: 100%;
    height: 180px;
  }

  .job-info {
    padding: 16px;
  }

  .job-title {
    font-size: 16px;
  }

  .job-actions {
    flex-direction: row;
    border-left: none;
    border-top: 1px solid #f2f4f6;
    padding: 12px 16px;
  }

  .btn-edit,
  .btn-delete {
    flex: 1;
  }

  .modal-content {
    padding: 24px;
  }
}

@media (max-width: 480px) {
  .page-header {
    margin-bottom: 24px;
  }

  .page-title {
    font-size: 20px;
  }

  .btn-create {
    width: 100%;
    justify-content: center;
  }

  .job-thumbnail {
    height: 160px;
  }

  .job-meta {
    flex-wrap: wrap;
  }

  .job-stats {
    gap: 12px;
    flex-wrap: wrap;
  }

  .upgrade-status,
  .banner-status {
    gap: 6px;
  }

  .upgrade-badge,
  .banner-badge {
    padding: 4px 8px;
    font-size: 11px;
  }

  .modal-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .modal-content {
    max-width: 100%;
    border-radius: 20px 20px 0 0;
    padding: 24px 20px calc(24px + env(safe-area-inset-bottom));
  }
}

/* Touch device hover 비활성화 */
@media (hover: none) {
  .job-item:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }

  .btn-create:hover {
    background: #3182f6;
  }

  .btn-edit:hover {
    background: #f2f4f6;
    color: #4e5968;
  }

  .btn-delete:hover {
    background: #fff;
    border-color: #ffc9c9;
  }
}
</style>